import React, {useState, useEffect} from "react";
import PropTypes from 'prop-types';
import {withStyles} from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import moment from "moment-timezone";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {KeyboardDatePicker, KeyboardDateTimePicker} from "@material-ui/pickers";
import {themeTypeToBool} from '../../../../../../../utils/helpers';
import t from "../../../../../../../languages/pl";
import {displayDateTimeFormat, dateTimePickerFormat} from '../../../../../../../utils/staticData';
import {FormControlLabel, Typography} from "@material-ui/core";
import {compose} from "recompose";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import ActionCreator from '../../../../../../../store/actions/index';
import CustomCheckbox from '../../../../../../Common/CustomCheckbox';
import {maxDateRange} from '../../../../../../../utils/staticData';

const styles = theme => ({
    container: {
        display: "flex",
        flexWrap: "wrap",
        flexDirection: "row",
        paddingBottom: theme.spacing(1),
        alignItems: "center",
        justifyContent: "flex-start"
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        width: 225
    },
    descriptionTextField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        width: 225
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120
    },
    formsContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start"
    },
    inputField: {
        margin: theme.spacing(1),
        width: 225
    },
    label: {
        color: themeTypeToBool(theme.palette.type) ? "#9E9E9E" : "#FAFAFA",
    },
    checkbox: {
        color: "grey"
    },
    currencyColor: {
        color: themeTypeToBool(theme.palette.type) ? "#9E9E9E" : "#FAFAFA",
    }
});

const InputField = withStyles(theme => ({
        root: {
            margin: theme.spacing(1),
            width: 225
        }
    }), {withTheme: true}
)(props => <TextField
    InputLabelProps={{
        style: {color: themeTypeToBool(props.theme.palette.type) ? "#9E9E9E" : "#FAFAFA"}
    }}
    {...props} />);

const RentForm = props => {
    const {classes, rent, isEdit, clients, checkRentValidation, checkClientNameValidation, onCheckboxChange} = props;
    const [state, setState] = useState(rent);
    const [infiniteRent, setInfiniteRent] = useState(false);
    const [finishTimeStore, setFinishTime] = useState(moment().unix());
    const options = clients.map(client => client.klient_nazwa);
    options.unshift("");

    useEffect(() => {
        if (!rent)
            return;
        if (isEdit) {
            setState(rent);
            if (rent.data_do === 0)
                onCheckboxChange(true);
            else
                onCheckboxChange(false);
        } else {
            setState(initialCarRent);
            onCheckboxChange(true);
        }
        checkRentValidation();
    }, [rent]);

    useEffect(() => {
        if (state.data_do === 0) {
            setInfiniteRent(true);
        } else if (state.data_do < state.data_od) {
            setFinishTime(state.data_od);
        } else if (state.data_do !== 0) {
            setFinishTime(state.data_do);
            setInfiniteRent(false);
        }
    }, [state.data_do]);

    const handleRentChange = e => {
        e.persist();
        rent[e.target.name] = e.target.value;

        setState(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value
        }));
    };

    const handleCheckboxChange = e => {
        setInfiniteRent(e.target.checked);
        onCheckboxChange(e.target.checked);
        if (!e.target.checked) {
            rent.data_do = finishTimeStore;

            setState(prevState => ({
                ...prevState,
                data_do: finishTimeStore
            }));
        } else {
            rent.data_do = 0;
        }
        if (infiniteRent && state.data_od > finishTimeStore) {
            setFinishTime(state.data_od);
        }
    };

    const handleContractChange = e => {
        e.persist();
        if (e.target.name === "umowa_nr" && parseInt(e.target.value) === 0) {
            rent.umowa_nr = 0;
            rent.umowa_mc = 0;
            rent.umowa_rok = 0;

            setState(prevState => ({
                ...prevState,
                [e.target.name]: parseInt(e.target.value),
                umowa_mc: 0,
                umowa_rok: 0,
            }));
        } else {
            rent[e.target.name] = parseInt(e.target.value);

            setState(prevState => ({
                ...prevState,
                [e.target.name]: parseInt(e.target.value)
            }));
        }
    };

    const handleRentDateChange = (propertyName, date) => {
        rent[propertyName] = moment(date).unix();

        setState(prevState => ({
            ...prevState,
            [propertyName]: moment(date).unix()
        }));
        checkRentValidation();
    };

    const handleClientChange = e => {
        e.persist();
        rent.klient[e.target.name] = e.target.value;

        setState(prevState => ({
            ...prevState,
            klient: {...prevState.klient, [e.target.name]: e.target.value}
        }));
    };

    const handleClientNameChange = e => {
        e.persist();
        rent.klient[e.target.name] = e.target.value;

        setState(prevState => ({
            ...prevState,
            klient: {...prevState.klient, [e.target.name]: e.target.value}
        }));
        checkClientNameValidation();
    };

    const handleDriverChange = e => {
        e.persist();

        let driverPropertyName = e.target.name;
        driverPropertyName = driverPropertyName.replace('kierowca1_', '');

        rent.kierowca[driverPropertyName] = e.target.value;

        setState(prevState => ({
            ...prevState,
            kierowca: {...prevState.kierowca, [driverPropertyName]: e.target.value}
        }));
    };

    const handleDriverDateChange = (propertyName, date) => {
        let driverPropertyName = propertyName;
        driverPropertyName = driverPropertyName.replace('kierowca1_', '');
        rent.kierowca[driverPropertyName] = moment(date).unix();

        setState(prevState => ({
            ...prevState,
            kierowca: {...prevState.kierowca, [driverPropertyName]: moment(date).unix()}
        }));
    };

    const handleAdditionalDriverChange = e => {
        e.persist();

        let additionalDriverPropertyName = e.target.name;
        additionalDriverPropertyName = additionalDriverPropertyName.replace('kierowca2_', '');

        rent.kierowca_dodatkowy[additionalDriverPropertyName] = e.target.value;

        setState(prevState => ({
            ...prevState,
            kierowca_dodatkowy: {...prevState.kierowca_dodatkowy, [additionalDriverPropertyName]: e.target.value}
        }));
    };

    const handleAdditionalDriverDateChange = (propertyName, date) => {
        let driverPropertyName = propertyName;
        driverPropertyName = driverPropertyName.replace('kierowca2_', '');
        rent.kierowca_dodatkowy[driverPropertyName] = moment(date).unix();

        setState(prevState => ({
            ...prevState,
            kierowca_dodatkowy: {...prevState.kierowca_dodatkowy, [driverPropertyName]: moment(date).unix()}
        }));
    };

    const handleAutocompleteChange = (client) => {
        if (client) {
            rent.klient.nazwa = client.klient_nazwa;
            rent.klient.rodzaj = client.klient_rodzaj === "Osoba prywatna" ? 0 : client.klient_rodzaj === "Firma" ? 1 : 2;
            rent.klient.ulica = client.klient_ulica;
            rent.klient.kod = client.klient_kod;
            rent.klient.miasto = client.klient_miasto;
            rent.klient.nip = client.klient_nip;
            rent.klient.pesel = client.klient_pesel;
            rent.klient.dowod = client.klient_dowod;
            rent.klient.email = client.klient_email;
            rent.klient.regon = client.klient_regon;
            rent.klient.telefon = client.klient_telefon;

            setState(prevState => ({
                ...prevState,
                klient: {
                    ...prevState.klient,
                    dowod: client.klient_dowod,
                    email: client.klient_email,
                    kod: client.klient_kod,
                    miasto: client.klient_miasto,
                    nazwa: client.klient_nazwa,
                    nip: client.klient_nip,
                    pesel: client.klient_pesel,
                    regon: client.klient_regon,
                    rodzaj: client.klient_rodzaj === "Osoba prywatna" ? 0 : client.klient_rodzaj === "Firma" ? 1 : 2,
                    telefon: client.klient_telefon,
                    ulica: client.klient_ulica
                }
            }));
        } else {
            rent.klient = Object.assign({}, initialCarRent.klient);
            setState(prevState => ({
                ...prevState,
                klient: Object.assign({}, initialCarRent.klient)
            }));
        }
        checkClientNameValidation();
    };


    return (
        <div className={classes.formsContainer}>
            <form className={classes.container}>
                <Autocomplete
                    //value={state.klient ? state.klient : ""}
                    noOptionsText={t.noClients}
                    classes={{inputRoot: classes.inputField}}
                    options={clients}
                    onChange={(event, value) => handleAutocompleteChange(value)}
                    getOptionLabel={option => option.klient_nazwa}
                    renderInput={params => <TextField {...params}
                                                      label={t.selectClient}
                                                      InputLabelProps={{
                                                          classes: {root: classes.label},
                                                          style: {marginLeft: 8}
                                                      }}
                    />}
                />

                <KeyboardDateTimePicker
                    className={classes.inputField}
                    ampm={false}
                    //variant="inline"
                    name="data_od"
                    value={state.data_od === 0 ? null : moment.unix(state.data_od).format(dateTimePickerFormat)}
                    maxDate={infiniteRent ? moment.unix(maxDateRange) : moment.unix(finishTimeStore)}
                    maxDateMessage={t.maxDateMessage}
                    invalidDateMessage={t.invalidDateFormat}
                    onChange={(date) => handleRentDateChange("data_od", date)}
                    label={t.from}
                    // onError={console.error}
                    format={displayDateTimeFormat}
                />
                <KeyboardDateTimePicker
                    className={classes.inputField}
                    ampm={false}
                    //variant="inline"
                    name="data_do"
                    value={infiniteRent ? null : moment.unix(finishTimeStore).format(dateTimePickerFormat)}
                    minDate={infiniteRent ? moment.unix(0) : moment.unix(state.data_od)}
                    minDateMessage={t.minDateMessage}
                    invalidDateMessage={t.invalidDateFormat}
                    onChange={(date) => handleRentDateChange('data_do', date)}
                    label={t.to}
                    // onError={console.error}
                    format={displayDateTimeFormat}
                    disabled={infiniteRent}
                />
                <FormControl className={classes.textField}>
                    <FormControlLabel
                        label={t.noEndDate}
                        control={
                            <CustomCheckbox
                                className={classes.checkbox}
                                checked={infiniteRent}
                                onChange={handleCheckboxChange}
                            />
                        }
                    />
                </FormControl>
                <InputField
                    onChange={handleRentChange}
                    label={t.kilometersLimit}
                    type="number"
                    name="limitkm"
                    value={state.limitkm ? state.limitkm : ""}
                />
                <FormControl className={classes.textField}>
                    <InputLabel className={classes.label}>Limit</InputLabel>
                    <Select
                        onChange={handleRentChange}
                        value={state.limitkm_rodzaj}
                        inputProps={{
                            name: "limitkm_rodzaj"
                        }}
                    >
                        <MenuItem value="0">Całkowity</MenuItem>
                        <MenuItem value="1">Dobowy</MenuItem>
                        <MenuItem value="2">Za dzień kalendarzowy</MenuItem>
                    </Select>
                </FormControl>
                <InputField
                    onChange={handleRentChange}
                    label={t.carRegistrationNumberUnderRepair}
                    className={classes.textField}
                    value={state.nr_rej_w_naprawie ? state.nr_rej_w_naprawie : ""}
                    name="nr_rej_w_naprawie"
                />
                <InputField
                    onChange={handleRentChange}
                    label={t.ratePerKmAboveLimit}
                    className={classes.textField}
                    InputProps={{
                        endAdornment: <InputAdornment position="end"><Typography
                            className={classes.currencyColor}>PLN</Typography></InputAdornment>
                    }}
                    type="number"
                    value={state.stawka_pow_limit ? state.stawka_pow_limit : ""}
                    name="stawka_pow_limit"
                />
                <InputField
                    onChange={handleRentChange}
                    label={t.lumSum}
                    className={classes.textField}
                    type="number"
                    InputProps={{
                        endAdornment: <InputAdornment position="end"><Typography
                            className={classes.currencyColor}>PLN</Typography></InputAdornment>
                    }}
                    value={state.ryczalt ? state.ryczalt : ""}
                    name="ryczalt"
                />
                <FormControl className={classes.textField}>
                    <InputLabel className={classes.label}>Ryczałt za</InputLabel>
                    <Select
                        onChange={handleRentChange}
                        value={state.ryczalt_rodzaj}
                        inputProps={{
                            name: "ryczalt_rodzaj"
                        }}
                    >
                        <MenuItem value="0">Za całość</MenuItem>
                        <MenuItem value="1">Za godzinę</MenuItem>
                        <MenuItem value="2">Za dzień</MenuItem>
                    </Select>
                </FormControl>
                <InputField
                    onChange={handleRentChange}
                    label={t.serviceOrderNumber}
                    className={classes.textField}
                    value={state.nr_zlecenia ? state.nr_zlecenia : ""}
                    name="nr_zlecenia"
                />
                {isEdit ?
                    <React.Fragment>
                        <InputField
                            onChange={handleContractChange}
                            type="number"
                            label={t.contractNumber}
                            className={classes.descriptionTextField}
                            value={state.umowa_nr}
                            InputProps={{inputProps: {min: 0}}}
                            name="umowa_nr"
                        />
                        <InputField
                            onChange={handleContractChange}
                            type="number"
                            label={t.monthOfContract}
                            disabled={state.umowa_nr === 0}
                            InputProps={{inputProps: {min: 1, max: 12}}}
                            className={classes.descriptionTextField}
                            value={state.umowa_mc}
                            name="umowa_mc"
                        />
                        <InputField
                            onChange={handleContractChange}
                            type="number"
                            label={t.yearOfContract}
                            disabled={state.umowa_nr === 0}
                            className={classes.descriptionTextField}
                            value={state.umowa_rok}
                            name="umowa_rok"
                        />
                    </React.Fragment>
                    : null
                }
                <InputField
                    onChange={handleRentChange}
                    label={t.description}
                    className={classes.descriptionTextField}
                    value={state.opis ? state.opis : ""}
                    name="opis"
                />
            </form>
            <h3>Klient</h3>
            <form className={classes.container} id="client">
                <InputField
                    label={t.nameAndSurnameOrBusinessName}
                    className={classes.textField}
                    value={state.klient.nazwa ? state.klient.nazwa : ""}
                    name="nazwa"
                    onChange={handleClientNameChange}
                    required={true}
                    error={props.isError && state.klient.nazwa === "" ? true : false}
                    helperText={props.isError && state.klient.nazwa === "" ? t.requiredField : null}
                />

                <FormControl className={classes.textField}>
                    <InputLabel className={classes.label}>Rodzaj</InputLabel>
                    <Select
                        inputProps={{
                            name: "rodzaj"
                        }}
                        onChange={handleClientChange}
                        value={state.klient.rodzaj}
                    >
                        <MenuItem value={0}>Osoba prywatna</MenuItem>
                        <MenuItem value={1}>Firma</MenuItem>
                        <MenuItem value={2}>Pracownik</MenuItem>
                    </Select>
                </FormControl>

                <InputField
                    onChange={handleClientChange}
                    name="ulica"
                    label={t.streetAndHouseNumber}
                    className={classes.textField}
                    value={state.klient.ulica ? state.klient.ulica : ""}
                />
                <InputField
                    label={t.zipCode}
                    className={classes.textField}
                    value={state.klient.kod ? state.klient.kod : ""}
                    name="kod"
                    onChange={handleClientChange}
                />
                <InputField
                    label={t.place}
                    className={classes.textField}
                    value={state.klient.miasto ? state.klient.miasto : ""}
                    onChange={handleClientChange}
                    name="miasto"
                />

                <InputField
                    label={t.peselOrDateOfBirth}
                    className={classes.textField}
                    value={state.klient.pesel ? state.klient.pesel : ""}
                    onChange={handleClientChange}
                    name="pesel"
                />
                <InputField
                    label={t.seriesAndNumberOfTheIdentityCard}
                    className={classes.textField}
                    name="dowod"
                    value={state.klient.dowod ? state.klient.dowod : ""}
                    onChange={handleClientChange}
                />
                <InputField
                    value={state.klient.nip ? state.klient.nip : ""}
                    label={t.nip}
                    className={classes.textField}
                    onChange={handleClientChange}
                    name="nip"
                />
                <InputField
                    label={t.phone}
                    className={classes.textField}
                    value={state.klient.telefon ? state.klient.telefon : ""}
                    name="telefon"
                    onChange={handleClientChange}
                />
                <InputField
                    label={t.email}
                    className={classes.textField}
                    value={state.klient.email ? state.klient.email : ""}
                    onChange={handleClientChange}
                    name="email"
                />
                <InputField
                    label={t.regon}
                    className={classes.textField}
                    value={state.klient.regon ? state.klient.regon : ""}
                    name="regon"
                    onChange={handleClientChange}
                />
            </form>

            <h3>Kierowca</h3>
            <form className={classes.container}>
                <InputField
                    label={t.firstAndLastName}
                    className={classes.textField}
                    value={state.kierowca.imie ? state.kierowca.imie : ""}
                    onChange={handleDriverChange}
                    name="kierowca1_imie"
                />
                <InputField
                    label={t.streetAndHouseNumber}
                    className={classes.textField}
                    value={state.kierowca.ulica ? state.kierowca.ulica : ""}
                    onChange={handleDriverChange}
                    name="kierowca1_ulica"
                />
                <InputField
                    label={t.zipCode}
                    className={classes.textField}
                    value={state.kierowca.kod ? state.kierowca.kod : ""}
                    onChange={handleDriverChange}
                    name="kierowca1_kod"
                />
                <InputField
                    value={state.kierowca.miasto ? state.kierowca.miasto : ""}
                    label={t.city}
                    className={classes.textField}
                    onChange={handleDriverChange}
                    name="kierowca1_miasto"
                />
                <InputField
                    label={t.driverLicenseNumber}
                    className={classes.textField}
                    value={state.kierowca.prawo ? state.kierowca.prawo : ""}
                    name="kierowca1_prawo"
                    onChange={handleDriverChange}
                />

                <KeyboardDatePicker
                    className={classes.inputField}
                    ampm={false}
                    variant="inline"
                    name="kierowca1_data_prawa"
                    disabled={state.kierowca.prawo === ""}
                    value={state.kierowca.data_prawa === 0 ? null : moment.unix(state.kierowca.data_prawa).format("YYYY-MM-DD")}
                    onChange={(date) => handleDriverDateChange('kierowca1_data_prawa', date)}
                    label={t.dateOfIssueOfTheDrivingLicense}
                    invalidDateMessage={t.invalidDateFormat}
                    // onError={console.error}
                    format="YYYY-MM-DD"
                />
                <InputField
                    label={t.authorityissuingTheDrivingLicense}
                    className={classes.textField}
                    name="kierowca1_organ"
                    value={state.kierowca.organ ? state.kierowca.organ : ""}
                    onChange={handleDriverChange}
                />
                <InputField
                    label={t.peselOrDateOfBirth}
                    className={classes.textField}
                    value={state.kierowca.pesel ? state.kierowca.pesel : ""}
                    name="kierowca1_pesel"
                    onChange={handleDriverChange}
                />
                <InputField
                    label={t.phone}
                    className={classes.textField}
                    value={state.kierowca.telefon ? state.kierowca.telefon : ""}
                    onChange={handleDriverChange}
                    name="kierowca1_telefon"
                />
            </form>

            <h3>Kierowca dodatkowy</h3>
            <form className={classes.container}>
                <InputField
                    label={t.firstAndLastName}
                    className={classes.textField}
                    value={state.kierowca_dodatkowy.imie ? state.kierowca_dodatkowy.imie : ""}
                    onChange={handleAdditionalDriverChange}
                    name="kierowca2_imie"
                />
                <InputField
                    label={t.streetAndHouseNumber}
                    className={classes.textField}
                    value={state.kierowca_dodatkowy.ulica ? state.kierowca_dodatkowy.ulica : ""}
                    onChange={handleAdditionalDriverChange}
                    name="kierowca2_ulica"
                />
                <InputField
                    label={t.zipCode}
                    className={classes.textField}
                    value={state.kierowca_dodatkowy.kod ? state.kierowca_dodatkowy.kod : ""}
                    onChange={handleAdditionalDriverChange}
                    name="kierowca2_kod"
                />
                <InputField
                    value={state.kierowca_dodatkowy.miasto ? state.kierowca_dodatkowy.miasto : ""}
                    label={t.city}
                    className={classes.textField}
                    onChange={handleAdditionalDriverChange}
                    name="kierowca2_miasto"
                />
                <InputField
                    label={t.driverLicenseNumber}
                    className={classes.textField}
                    value={state.kierowca_dodatkowy.prawo ? state.kierowca_dodatkowy.prawo : ""}
                    name="kierowca2_prawo"
                    onChange={handleAdditionalDriverChange}
                />

                <KeyboardDatePicker
                    className={classes.inputField}
                    ampm={false}
                    variant="inline"
                    name="czas_od"
                    disabled={state.kierowca_dodatkowy.prawo === ""}
                    value={state.kierowca_dodatkowy.data_prawa === 0 ? null : moment.unix(state.kierowca_dodatkowy.data_prawa).format("YYYY-MM-DD")}
                    onChange={(date) => handleAdditionalDriverDateChange('kierowca2_data_prawa', date)}
                    label={t.dateOfIssueOfTheDrivingLicense}
                    invalidDateMessage={t.invalidDateFormat}
                    // onError={console.error}
                    format="YYYY-MM-DD"
                />
                <InputField
                    label={t.authorityissuingTheDrivingLicense}
                    className={classes.textField}
                    name="kierowca2_organ"
                    value={state.kierowca_dodatkowy.organ ? state.kierowca_dodatkowy.organ : ""}
                    onChange={handleAdditionalDriverChange}
                />
                <InputField
                    label={t.peselOrDateOfBirth}
                    className={classes.textField}
                    value={state.kierowca_dodatkowy.pesel ? state.kierowca_dodatkowy.pesel : ""}
                    name="kierowca2_pesel"
                    onChange={handleAdditionalDriverChange}
                />
                <InputField
                    label={t.phone}
                    className={classes.textField}
                    value={state.kierowca_dodatkowy.telefon ? state.kierowca_dodatkowy.telefon : ""}
                    onChange={handleAdditionalDriverChange}
                    name="kierowca2_telefon"
                />
            </form>
        </div>
    );
};

RentForm.propTypes = {
    classes: PropTypes.object,
    rent: PropTypes.object,
    clients: PropTypes.array,
    checkRentValidation: PropTypes.func,
    checkClientNameValidation: PropTypes.func,
    onCheckboxChange: PropTypes.func,
    isError: PropTypes.bool,
    isEdit: PropTypes.bool
};

const mapStateToProps = (state) => ({
    //clients: state.rentReducer.clients
});

const mapDispatchToProps = dispatch => {
    return bindActionCreators(ActionCreator, dispatch);
};

const initialCarRent = {
    data_do: 0,
    data_od: 0,
    kierowca: {
        data_prawa: 0,
        imie: "",
        kod: "",
        miasto: "",
        organ: "",
        pesel: "",
        prawo: "",
        telefon: "",
        ulica: ""
    },
    kierowca_dodatkowy: {
        data_prawa: 0,
        imie: "",
        kod: "",
        miasto: "",
        organ: "",
        pesel: "",
        prawo: "",
        telefon: "",
        ulica: ""
    },
    klient: {
        dowod: "",
        email: "",
        kod: "",
        miasto: "",
        nazwa: "",
        nip: "",
        pesel: "",
        regon: "",
        rodzaj: 0,
        telefon: "",
        ulica: ""
    },
    limitkm: 0,
    limitkm_rodzaj: 0,
    limitkm_rodzaj_lista: [],
    nr_rej_w_naprawie: "",
    umowa_nr: 0,
    umowa_mc: 0,
    umowa_rok: 0,
    nr_zlecenia: "",
    opis: "",
    ryczalt: "",
    ryczalt_rodzaj: 0,
    ryczalt_rodzaj_lista: [],
    stawka_pow_limit: "",
    wynajem_id: 0
};

export default compose(
    withStyles(styles),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(RentForm);